import { useState, useEffect, useContext } from 'react';
import { Col, Row, Select, Tag, Alert, DatePicker, Card } from 'antd-v4';

// Icons
import { IoIosNotificationsOutline } from 'react-icons/io';

// Charts (& config)
import Chart from 'react-apexcharts';
import { chartTimelineSMS } from './charts/chartTimelineSMS';
import { chartTimelineContacts } from './charts/chartTimelineContacts';
import { chartBarDeliverability } from './charts/chartBarDeliverability';

// Other libs
import moment from 'moment/min/moment-with-locales';
import { formatNumber } from '@leedpulse-monorepo/utils';
import { UserType } from '@leedpulse-monorepo/enums';

// Components
import FeatureCard from '../../layout/components/feature-card';
import ProspectsInterestProgressBar from './ui/ProspectsInterestProgressBar';
import LeadsCard from './ui/LeadsCard';
import Loader from '../components/loader';
import FullWidthCard from '../../layout/components/fullwidth-card';
import MainCard from '../../layout/components/main-card';
import NotificationsFeedbackChart from './ui/NotificationsFeedbackChart';

// Hooks
import { useNavigate } from 'react-router-dom';

// Config
import { FirebaseContext } from '../../context/firebaseContext';
import { useSubscription } from '../../hooks/useSubscription';
import { useCustomise } from '../../context/customiseContext';
import { FieldTimeOutlined, FolderOutlined, SmileOutlined } from '@ant-design/icons';
import { message } from 'antd';
import { NotificationFeedbackType } from '@leedpulse-monorepo/enums';
import { BackendContext } from '../../context/backendContext';
import { searchNotificationsView } from '../../services/AlgoliaService';
import { getPlanById } from '../../services/SubscriptionService';

const { RangePicker } = DatePicker;

export default function Home() {
  // Hooks
  const {
    userData,
    countAllAndEngagedContacts,
    getHomeStats,
    getJobsByPeriod,
    getScenariosBySubscriptionId,
    getUsersBySubscriptionId,
  } = useContext(FirebaseContext);

  const { getRequest } = useContext(BackendContext);

  const { subscription } = useSubscription();
  const [isSubscriptionExpired, setIsSubscriptionExpired] = useState(false);

  // History
  let navigate = useNavigate();

  // State
  const [contactsCount, setContactsCount] = useState(null);
  const [engagedContactsCount, setEngagedContactsCount] = useState(0);
  const [notificationCount, setNotificationCount] = useState(null);
  const [notificationStatsByFeedback, setNotificationStatsByFeedback] = useState(null);
  const [notificationStatsLoading, setNotificationStatsLoading] = useState(false);
  const [homeStats, setHomeStats] = useState([]);
  const [planInfos, setPlanInfos] = useState(null);
  const [contactsStats, setContactsStats] = useState();
  const [dataReceivedSentSMS, setDataReceivedSentSMS] =
    useState(chartTimelineSMS); // Chart data about SENT and RECEIVED sms
  const [dataContactsEngaged, setDataContactsEngaged] = useState(
    chartTimelineContacts
  ); // Chart data about contacts engaged
  const [dataDeliverability, setDataDeliverability] = useState(
    chartBarDeliverability
  ); // Chart data about deliverability
  const [bShowCharts, setBShowCharts] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [scenarios, setScenarios] = useState([]);
  const [scenarioSelected, setScenarioSelected] = useState(null);
  const [collaborators, setCollaborators] = useState([]);
  const [collaboratorSelected, setCollaboratorSelected] = useState(null);
  const [period, setPeriod] = useState([]);
  const { theme } = useCustomise();

  useEffect(() => {
    if (userData && userData.type === 'user') {
      setCollaboratorSelected(userData.uid);
    }
  }, [userData]);

  useEffect(() => {
    if (subscription) {
      // Check if subscription has expired
      if (subscription.plan_end) {
        const planEndDate = moment(subscription.plan_end).startOf('day');
        const today = moment().startOf('day');
        setIsSubscriptionExpired(today.isAfter(planEndDate));
      }

      loadPlans(subscription?.id_plan);
      loadDashboard(subscription?.api_key);
      getScenariosBySubscriptionId(subscription?.id).then((oRes) => {
        setScenarios(oRes.doc ?? []);
      });
      getUsersBySubscriptionId(subscription?.id, true).then((oRes) => {
        setCollaborators(oRes.users);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscription]);

  useEffect(() => {
    setBShowCharts(false);
    loadStats();
  }, [collaboratorSelected, scenarioSelected]);

  // Load plan infos
  const loadPlans = async (idPlan) => {
    let res = await getPlanById(idPlan);
    if (res.status === 'success' && res.doc) {
      setPlanInfos(res.doc);
    }
  };

  // Get Dashboard optimized
  const loadDashboard = async (apiKey) => {
    // Independent blocks: contacts counters, notifications stats and jobs each
    // hit a different data source and don't depend on one another.
    await Promise.all([
      loadContactsCounts(apiKey),
      loadNotificationsStats(apiKey),
      loadJobs(apiKey),
    ]);
  };

  const loadContactsCounts = async (apiKey) => {
    const engagedContactsResponse = await countAllAndEngagedContacts(apiKey);

    if (engagedContactsResponse.success) {
      setContactsCount(engagedContactsResponse.doc);
      setEngagedContactsCount(engagedContactsResponse.engagedContactsCount);
    } else {
      message.error(
        'Une erreur est survenue lors de la récupération des contacts engagés'
      );
    }
  };

  const loadNotificationsStats = async (apiKey) => {
    // A single Algolia request returns every per-tab count at once
    setNotificationStatsLoading(true);
    try {
      const { counts, feedbackCounts } = await searchNotificationsView(getRequest, apiKey, {
        userType: userData?.type,
        userUid: userData?.uid,
        countsOnly: true,
      });

      setNotificationCount((counts.todo || 0).toString());
      // Feedbacks actually given, so a notification processed without one is not counted here
      setNotificationStatsByFeedback({
        [NotificationFeedbackType.UNREACHABLE]: feedbackCounts.unreachable || 0,
        [NotificationFeedbackType.TO_BE_CONTINUED]: feedbackCounts.to_be_continued || 0,
        [NotificationFeedbackType.APPOINTMENT_SCHEDULED]: feedbackCounts.appointment_scheduled || 0,
        [NotificationFeedbackType.NOTHING_TO_DO]: feedbackCounts.nothing_to_do || 0,
      });
    } catch (error) {
      console.error('Erreur lors de la récupération des notifications :', error);
      message.error(
        'Une erreur est survenue lors de la récupération des notifications'
      );
    }
    setNotificationStatsLoading(false);
  };

  const loadJobs = async (apiKey) => {
    setJobsLoading(true);
    const now = new Date();
    const endOfWeek = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate() + 7
    );

    const response = await getJobsByPeriod(
      now.getTime(),
      endOfWeek.getTime(),
      apiKey
    );

    if (response.success) {
      setJobs(response.doc);
    } else {
      message.error('Une erreur est survenue lors de la récupération des jobs');
    }

    setJobsLoading(false);
  };

  // Load Stats optimized
  const loadStats = async () => {
    if (subscription) {
      let resStats = await getHomeStats(
        subscription?.api_key,
        collaboratorSelected,
        scenarioSelected,
        period
      );
      if (resStats.status === 'success' && resStats.doc) {
        setHomeStats(resStats.doc);
      }
    }
  };

  useEffect(() => {
    if (period.length === 0) return;
    setBShowCharts(false);
    loadStats();
  }, [period]);

  useEffect(() => {
    if (!homeStats) return;

    // ---------------------------------------------------------
    // CHART: Contacts engaged counters by period
    // ---------------------------------------------------------
    const contactsEngagedData = homeStats.find(
      (item) => item.type === 'ContactsEngagedStats'
    );
    const contactsCreatedData = homeStats.find(
      (item) => item.type === 'ContactsCreatedStats'
    );

    if (!contactsEngagedData || !contactsCreatedData) return;

    const aContactsEngagedForPeriod = contactsEngagedData.data;
    const aContactsCreatedForPeriod = contactsCreatedData.data;

    const nbContactsDays = aContactsEngagedForPeriod.length;
    let aContactsCategories = [];
    let aGroupedContactsEngaged = [];
    let aGroupedContactsCreated = [];

    if (nbContactsDays <= 90) {
      // ➤ Affichage quotidien
      const endContactDate = period[0].clone();
      aContactsEngagedForPeriod.forEach(() => {
        aContactsCategories.push(
          endContactDate.add(1, 'days').format('Do MMM')
        );
      });

      aGroupedContactsEngaged = aContactsEngagedForPeriod;
      aGroupedContactsCreated = aContactsCreatedForPeriod;
    } else {
      // ➤ Regroupement mensuel
      const grouped = {};
      const endContactDate = period[0].clone();
      for (let i = 0; i < nbContactsDays; i++) {
        const date = endContactDate.add(1, 'days');
        const key = date.format('MMM YYYY');

        if (!grouped[key]) {
          grouped[key] = { engaged: 0, created: 0 };
        }

        grouped[key].engaged += aContactsEngagedForPeriod[i];
        grouped[key].created += aContactsCreatedForPeriod[i];
      }

      aContactsCategories = Object.keys(grouped);
      aGroupedContactsEngaged = aContactsCategories.map(
        (month) => grouped[month].engaged
      );
      aGroupedContactsCreated = aContactsCategories.map(
        (month) => grouped[month].created
      );
    }

    // ➤ Construction du graphe
    let oContactEngagedData = { ...chartTimelineContacts };
    oContactEngagedData.series[0].data = aGroupedContactsEngaged;
    oContactEngagedData.series[1].data = aGroupedContactsCreated;
    oContactEngagedData.options.xaxis.categories = aContactsCategories;

    setDataContactsEngaged(oContactEngagedData);

    // ---------------------------------------------------------
    // CHART: Message counters by period
    // ---------------------------------------------------------
    const smsReceivedData = homeStats.find(
      (item) => item.type === 'SMSReceivedPeriodSliced'
    );
    const smsSentData = homeStats.find(
      (item) => item.type === 'SMSSentPeriodSliced'
    );

    if (!smsReceivedData || !smsSentData) return;

    const aReceivedSMSForPeriod = smsReceivedData.data;
    const aSentSMSForPeriod = smsSentData.data;

    const nbDays = aReceivedSMSForPeriod.length;
    let aCategories = [];
    let aGroupedReceived = [];
    let aGroupedSent = [];

    if (nbDays <= 90) {
      // ➤ Affichage quotidien
      const endDate = period[0].clone();
      aReceivedSMSForPeriod.forEach(() => {
        aCategories.push(endDate.add(1, 'days').format('Do MMM'));
      });

      aGroupedReceived = aReceivedSMSForPeriod;
      aGroupedSent = aSentSMSForPeriod;
    } else {
      // ➤ Regroupement mensuel
      const grouped = {};
      const endDate = period[0].clone();
      for (let i = 0; i < nbDays; i++) {
        const date = endDate.add(1, 'days');
        const key = date.format('MMM YYYY');

        if (!grouped[key]) {
          grouped[key] = { sent: 0, received: 0 };
        }

        grouped[key].received += aReceivedSMSForPeriod[i];
        grouped[key].sent += aSentSMSForPeriod[i];
      }

      aCategories = Object.keys(grouped);
      aGroupedSent = aCategories.map((month) => grouped[month].sent);
      aGroupedReceived = aCategories.map((month) => grouped[month].received);
    }

    // ➤ Construction du graphe
    let oSMSData = { ...chartTimelineSMS };
    oSMSData.series[0].data = aGroupedSent;
    oSMSData.series[1].data = aGroupedReceived;
    oSMSData.options.xaxis.categories = aCategories;

    setDataReceivedSentSMS(oSMSData);

    // ---------------------------------------------------------
    // CHART: Deliverability
    // ---------------------------------------------------------

    // Retrieve the deliverability stats from homeStats
    const deliverabilityStats = homeStats.find(
      (item) => item.type === 'DeliverabilityStats'
    );

    if (!deliverabilityStats) return;

    // Destructure the deliverability data
    const { prcentErrors, prcentNotResponders, prcentResponders } =
      deliverabilityStats.data;

    // Create a new set of data for the chart
    let oDeliverabilityData = { ...chartBarDeliverability };

    // Update the series data with the deliverability percentages
    oDeliverabilityData.series[2].data = [prcentErrors];
    oDeliverabilityData.series[1].data = [prcentNotResponders];
    oDeliverabilityData.series[0].data = [prcentResponders];

    // Apply the new data to the chart
    setDataDeliverability(oDeliverabilityData);

    // ---------------------------------------------------------
    // CHART: Contacts Interest
    // ---------------------------------------------------------
    setContactsStats(homeStats.find((item) => item.type === 'ContactsStats'));

    setTimeout(() => {
      // Force redesign of Apex chart
      setBShowCharts(true);
      window.dispatchEvent(new Event('resize'));
    }, 1000);
  }, [homeStats]);

  useEffect(() => {
    if (!subscription) return;

    // Load the charts for the default period as 90 days (3 months)
    setPeriod([moment().subtract(3, 'months'), moment()]);
  }, [subscription]);

  // ---------------------------------------------------------
  // CHART: Jobs
  // ---------------------------------------------------------

  const displayJobsTitle = () => {
    const now = new Date();
    const endOfDay = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      23,
      59,
      59,
      59
    );
    const aJobsOfTheDay = jobs.filter(
      (job) => job.timestamp <= endOfDay.getTime()
    );
    return aJobsOfTheDay.length === 0 ? 'Prochainement' : "Aujourd'hui";
  };

  const displayJobsNumber = () => {
    const now = new Date();
    const endOfDay = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      23,
      59,
      59,
      59
    );
    const aJobsOfTheDay = jobs.filter(
      (job) => job.timestamp <= endOfDay.getTime()
    );
    return aJobsOfTheDay.length > 0 ? aJobsOfTheDay.length : jobs.length;
  };

  const hasNotificationsStatsByFeedback = notificationStatsByFeedback && Object.values(notificationStatsByFeedback).reduce((sum, count) => sum + count, 0) > 0 ? true : false;

  const notificationsStatsByFeedbackCount = notificationStatsByFeedback && Object.values(notificationStatsByFeedback).reduce((sum, count) => sum + count, 0);

  return (
    <Row className="hp-content-main hp-pt-md-80 hp-py-64 hp-px-xs-0 hp-px-lg-32">
      <Col span={24}>
        <Row gutter={[32, 32]}>
          {isSubscriptionExpired && (
            <Col span={24}>
              <Alert
                message="Votre abonnement a expiré"
                description="Vous devez renouveler votre abonnement pour continuer à utiliser toutes les fonctionnalités"
                type="error"
                showIcon
                className="hp-mb-16"
              />
            </Col>
          )}

          <Col span={24}>
            <Row align="middle" justify="space-between">
              <Col md={12} span={24}>
                <span className="h1 hp-font-weight-600 hp-text-color-black-bg hp-text-color-dark-0 hp-d-block">
                  Bonjour {userData?.firstname || ''}
                </span>
                <span className="h3 hp-font-weight-100 hp-text-color-black-bg hp-text-color-dark-0 hp-d-block hp-mt-16">
                  {subscription?.name || ''}
                </span>
              </Col>
            </Row>
          </Col>

          {/* Alert if no scenario is configured */}
          {subscription?.id_scenarios?.length === 0 && (
            <Col span={24} className="hp-mb-16">
              <Alert
                message="Attention"
                description="Aucun scénario n'est configuré pour votre compte. Veuillez contacter un administrateur."
                type="error"
                showIcon
              />
            </Col>
          )}

          <Col md={hasNotificationsStatsByFeedback ? 14 : 12} span={24}>
            <Row gutter={[32, 32]}>
              {/* Nb of contacts */}
              <Col sm={12} span={24} onClick={() => navigate('/contacts')} style={{ display: 'flex' }}>
                <div style={{ width: '100%', height: '100%' }}>
                  <FeatureCard
                    icon={
                      <FolderOutlined
                        style={{ fontSize: 22 }}
                        className="hp-text-color-primary-1"
                      />
                    }
                    title="Contacts"
                    number={
                      contactsCount !== null && contactsCount !== undefined ? (
                        formatNumber(contactsCount)
                      ) : (
                        <Loader />
                      )
                    }
                    engagedNumber={
                      engagedContactsCount !== null &&
                        engagedContactsCount !== undefined ? (
                        formatNumber(engagedContactsCount)
                      ) : (
                        <Loader />
                      )
                    }
                    numberColor="#000"
                    engagedNumberColor="#888"
                  />
                </div>
              </Col>

              {/* Credits */}
              <Col sm={12} span={24} onClick={() => navigate('/contacts')} style={{ display: 'flex' }}>
                <div style={{ width: '100%', height: '100%' }}>
                  <LeadsCard
                    credits={subscription?.credits}
                    plan={planInfos}
                    userType={userData?.type}
                  />
                </div>
              </Col>

              {/* Nb of notifications */}
              <Col sm={hasNotificationsStatsByFeedback ? 12 : 24} span={24} onClick={() => navigate('/notifications')} style={{ display: 'flex' }}>
                <div style={{ width: '100%', height: '100%' }}>
                  <FeatureCard
                    icon={
                      <IoIosNotificationsOutline
                        size="28"
                        variant="Bold"
                        className="hp-text-color-primary-1 hp-text-color-dark-0"
                      />
                    }
                    title="Notifications à traiter"
                    number={
                      notificationCount != null ? (
                        formatNumber(notificationCount)
                      ) : (
                        <Loader />
                      )
                    }
                    numberColor={notificationCount > 0 ? '#ff0022' : '#000'}
                    extraTextButton={
                      notificationCount != null && notificationCount > 0
                        ? 'Traiter les notifications'
                        : 'Voir les notifications'
                    }
                    onExtraBtnClick={() => navigate('/notifications')}
                    extraButtonType={
                      notificationCount != null && notificationCount > 0
                        ? 'primary'
                        : 'link'
                    }
                  />
                </div>
              </Col>

              {/* Par feedback chart */}
              {hasNotificationsStatsByFeedback && (
                <Col sm={12} span={24} style={{ display: 'flex' }}>
                  <Card className="hp-border-radius-lg hp-dashboard-feature-card" style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
                    <div
                      className="h5 hp-mb-2 hp-d-block hp-text-color-black-bg hp-text-uppercase hp-text-color-dark-0 hp-font-weight-500"
                      style={{ marginBottom: '12px' }}
                    >
                      Notifications traitées ({notificationsStatsByFeedbackCount})
                    </div>
                    <div style={{ flex: 1, overflow: 'hidden' }}>
                      <NotificationsFeedbackChart
                        stats={notificationStatsByFeedback}
                        loading={notificationStatsLoading}
                      />
                    </div>
                  </Card>
                </Col>
              )}
            </Row>
          </Col>

          <Col md={hasNotificationsStatsByFeedback ? 10 : 12} span={24}>
            <Row gutter={[32, 32]}>
              <Col span={24}>
                <MainCard
                  title={displayJobsTitle()}
                  icon={
                    <FieldTimeOutlined
                      style={{ fontSize: 22 }}
                      className="hp-text-color-primary-1"
                    />
                  }
                  number={displayJobsNumber()}
                  numberColor={jobs.length > 0 ? 'green' : '#000'}
                  data={jobs}
                  onRefresh={() => {
                    loadJobs(subscription.api_key);
                  }}
                  loading={jobsLoading}
                />
              </Col>
            </Row>
          </Col>

          <Col span={24} className="hp-mb-18">
            <Row gutter={[32, 32]}>
              <Col md={24} span={24}>
                <span className="h4 hp-font-weight-600 hp-text-color-black-bg hp-text-color-dark-0 hp-d-block">
                  Statistiques
                </span>
              </Col>

              <Col md={userData?.type !== UserType.USER ? 8 : 12} span={24}>
                <RangePicker
                  disabled={!bShowCharts}
                  placeholder={['Début', 'Fin']}
                  format="DD/MM/YYYY"
                  value={period}
                  onChange={(dates) => {
                    if (dates) {
                      const [beginDate, endDate] = dates;
                      setPeriod([beginDate, endDate]);
                    } else {
                      setPeriod([]);
                    }
                  }}
                  style={{ width: '100%' }}
                />
              </Col>

              <Col md={userData?.type !== UserType.USER ? 8 : 12} span={24}>
                <Select
                  disabled={!bShowCharts}
                  options={[
                    {
                      value: '',
                      label: 'Tous les scénarios',
                    },
                    ...scenarios
                      .map((scenario) => {
                        return {
                          value: scenario.id,
                          label: scenario.name_short ?? scenario.name,
                        };
                      })
                      .sort((a, b) => a.label.localeCompare(b.label)),
                  ]}
                  placeholder="Tous les scénarios"
                  style={{ width: '100%' }}
                  value={scenarioSelected}
                  allowClear
                  onChange={(value) => setScenarioSelected(value)}
                ></Select>
              </Col>

              {userData?.type !== UserType.USER && (
                <Col md={8} span={24}>
                  <Select
                    disabled={!bShowCharts}
                    options={[
                      {
                        value: '',
                        label: 'Tous les collaborateurs',
                      },
                      ...collaborators.map((collaborator) => {
                        return {
                          value: collaborator.uid,
                          label: `${collaborator.firstname} ${collaborator.lastname}`,
                        };
                      }),
                    ]}
                    allowClear
                    placeholder="Tous les collaborateurs"
                    style={{ width: '100%' }}
                    value={collaboratorSelected}
                    onChange={(value) => setCollaboratorSelected(value)}
                  ></Select>
                </Col>
              )}

              {/* Progress Bar with prospects interest */}
              <ProspectsInterestProgressBar
                stats={
                  contactsStats
                    ? contactsStats
                    : {
                      data: {
                        interestedCount: 0,
                        consideringCount: 0,
                        notAvailableCount: 0,
                        notResponsiveCount: 0,
                      },
                    }
                }
              />

              {/* CHART: Nb of messages sent and received */}
              <Col span={24} md={24} lg={12}>
                <FullWidthCard
                  title="Nombre de contacts"
                  chart={
                    <>
                      <Col span={24}>
                        {bShowCharts ? (
                          <Chart
                            options={dataContactsEngaged.options}
                            series={dataContactsEngaged.series}
                            type="line"
                            height={350}
                          />
                        ) : (
                          <Row
                            style={{ height: 350 }}
                            align="middle"
                            justify="center"
                          >
                            <Loader />
                          </Row>
                        )}
                      </Col>
                      <p style={{ fontSize: '12px', color: '#777792' }}>
                        Statistique calculée sur les contacts ayant répondu ou
                        reçu au moins un message
                      </p>
                      <Row style={{ marginTop: '15px' }}>
                        <Tag color="green" className="hp-d-flex-full-center">
                          <span
                            style={{ fontSize: '44px' }}
                            className="remix-icon hp-text-color-success-1"
                          >
                            &#8226;
                          </span>{' '}
                          Engagés
                        </Tag>
                        <Tag
                          color={theme !== 'dark' ? 'lightgrey' : null}
                          className="hp-d-flex-full-center"
                        >
                          <span
                            style={{ fontSize: '44px' }}
                            className="remix-icon hp-text-color-black-1"
                          >
                            &#8226;
                          </span>{' '}
                          Créés
                        </Tag>
                      </Row>
                    </>
                  }
                />
              </Col>

              {/* CHART: Performance */}
              <Col span={24} md={24} lg={12}>
                <FullWidthCard
                  title="Performance"
                  chart={
                    <>
                      <Col span={24}>
                        {bShowCharts ? (
                          <Chart
                            options={dataDeliverability.options}
                            series={dataDeliverability.series}
                            type="bar"
                            width="100%"
                            height={350}
                          />
                        ) : (
                          <Row
                            style={{ height: 350 }}
                            align="middle"
                            justify="center"
                          >
                            <Loader />
                          </Row>
                        )}
                      </Col>
                      <p style={{ fontSize: '12px', color: '#777792' }}>
                        Statistique calculée sur les contacts ayant reçu au
                        moins un message
                      </p>
                      <Row style={{ marginTop: '15px' }}>
                        <Tag color="green" className="hp-d-flex-full-center">
                          <span
                            style={{ fontSize: '44px' }}
                            className="remix-icon hp-text-color-success-1"
                          >
                            &#8226;
                          </span>{' '}
                          Prospects ayant répondu
                        </Tag>
                      </Row>
                    </>
                  }
                />
              </Col>

              {/* CHART: Nb of messages sent and received */}
              {userData?.type !== UserType.USER && (
                <Col span={24} md={24} lg={24}>
                  <FullWidthCard
                    title="Nombre de messages"
                    chart={
                      <>
                        <Col span={24}>
                          {bShowCharts ? (
                            <Chart
                              options={dataReceivedSentSMS.options}
                              series={dataReceivedSentSMS.series}
                              type="line"
                              height={350}
                            />
                          ) : (
                            <Row
                              style={{ height: 350 }}
                              align="middle"
                              justify="center"
                            >
                              <Loader />
                            </Row>
                          )}
                        </Col>
                        <Row style={{ marginTop: '15px' }}>
                          <Tag color="purple" className="hp-d-flex-full-center">
                            <span
                              style={{ fontSize: '44px' }}
                              className="remix-icon hp-text-color-primary-1"
                            >
                              &#8226;
                            </span>{' '}
                            Envoyés
                          </Tag>
                          <Tag
                            color={theme !== 'dark' ? 'lightgrey' : null}
                            className="hp-d-flex-full-center"
                          >
                            <span
                              style={{ fontSize: '44px' }}
                              className="remix-icon hp-text-color-black-1"
                            >
                              &#8226;
                            </span>{' '}
                            Reçus
                          </Tag>
                        </Row>
                      </>
                    }
                  />
                </Col>
              )}
            </Row>
          </Col>
        </Row>
      </Col>
    </Row>
  );
}
